import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAdmin } from '../../context/AdminContext';
import styles from './Admin.module.css';

const sanitizeFileName = (name: string) => {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_") 
    .replace(/[()]/g, "") 
    .replace(/[^a-zA-Z0-9._-]/g, "")
    .toLowerCase();
};

export const AdminDashboard = () => {
  const { logout } = useAdmin();
  const [formData, setFormData] = useState({
    month: new Date().toLocaleString('cs-CZ', { month: 'long' }),
    rocnik: '',
    title: '',
    description: '',
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Kontrola typu souboru
      if (!file.type.startsWith('image/')) {
        setError('Prosím vyber obrázek!');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      const timestamp = Date.now();
      const safeName = sanitizeFileName(file.name);
      const fileName = `portfolio_${timestamp}_${safeName}`;
      
      console.log('Nahrávám obrázek:', fileName);
      console.log('Velikost souboru:', file.size, 'bytes');
      console.log('Typ souboru:', file.type);
      
      const { data, error: uploadError } = await supabase.storage
        .from('portfolio-images')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        console.error('❌ Chyba nahrávání:', uploadError);
        console.error('Detaily:', {
          message: uploadError.message,
          status: uploadError.status,
        });
        
        if (uploadError.status === 400) {
          throw new Error('400 Bad Request - Zkontroluj RLS politiky v Supabase Storage!');
        }
        if (uploadError.message?.includes('Bucket not found')) {
          throw new Error('Bucket "portfolio-images" neexistuje v Supabase!');
        }
        if (uploadError.message?.includes('not authenticated')) {
          throw new Error('Nemáš oprávnění pro nahrávání. Zkontroluj RLS politiky!');
        }
        throw uploadError;
      }

      console.log('✅ Obrázek nahrán:', data);

      // Získání veřejné URL obrázku
      const { data: urlData } = supabase.storage
        .from('portfolio-images')
        .getPublicUrl(fileName);

      console.log('📸 Veřejná URL:', urlData.publicUrl);
      return urlData.publicUrl;
    } catch (err) {
      console.error('❌ Upload selhalo:', err);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.month.trim() || !formData.rocnik.trim() || !formData.title.trim() || !formData.description.trim()) {
      setError('Vyplň všechna povinná pole!');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      let imageUrl: string | null = null;

      // Nahrání obrázku, pokud byl vybrán
      if (imageFile) {
        imageUrl = await uploadImage(imageFile);
        if (!imageUrl) {
          setError('Chyba při nahrávání obrázku. Pokud chceš, můžeš pokračovat bez něj.');
          // Pokračujeme bez obrázku
        }
      }

      const { error: insertError } = await supabase.from('school_portfolio_entries').insert([
        {
          month: formData.month,
          rocnik: formData.rocnik,
          title: formData.title,
          description: formData.description,
          image: imageUrl || null,
        },
      ]);

      if (insertError) {
        setError(`Chyba při ukládání: ${insertError.message}`);
        console.error(insertError);
      } else {
        setSuccess('Zápis byl úspěšně uložen! ✅');
        // Reset formuláře
        setFormData({
          month: new Date().toLocaleString('cs-CZ', { month: 'long' }),
          rocnik: '',
          title: '',
          description: '',
        });
        setImageFile(null);
        setImagePreview(null);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Neočekávaná chyba při ukládání.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className={styles.dashboard}>
        <div className={styles.dashboardHeader}>
          <h1>Admin Panel</h1>
          <button onClick={logout} className={styles.logoutBtn}>
            Odhlásit se
          </button>
        </div>

        <div className={styles.formSection}>
          <h2 >Přidat zápis do školního portfolia</h2>

          {error && (
            <div>
              {error}
            </div>
          )}
          {success && <div className={styles.success}>{success}</div>}

          <form onSubmit={handleSubmit}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="month" className={styles.label}>
                  Měsíc
                </label>
                <input
                  id="month"
                  type="text"
                  name="month"
                  value={formData.month}
                  onChange={handleChange}
                  className={styles.input}
                  placeholder="Např. Leden, Únor..."
                  disabled={loading}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="rocnik" className={styles.label}>
                  Ročník
                </label>
                <input
                  id="rocnik"
                  type="text"
                  name="rocnik"
                  value={formData.rocnik}
                  onChange={handleChange}
                  className={styles.input}
                  placeholder="Např. 1., 2., 3."
                  disabled={loading}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="title" className={styles.label}>
                  Nadpis
                </label>
                <input
                  id="title"
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={styles.input}
                  placeholder="Nadpis zápisu..."
                  disabled={loading}
                />
              </div>
            </div>

            <div className={[styles.formGroup, styles.formRowFull].join(' ')}>
              <label htmlFor="description" className={styles.label}>
                Popis
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className={styles.input}
                placeholder="Podrobný popis zápisu..."
                disabled={loading}
                style={{ minHeight: '120px', resize: 'vertical' }}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="image" className={styles.label}>
                Obrázek
              </label>
              <input
                id="image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className={styles.input && styles.fileInput}
                disabled={loading}
              />
              {imagePreview && (
                <div style={{ marginTop: '1rem' }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px' }}
                  />
                </div>
              )}
            </div>

            <button type="submit" className={styles.submitBtn} disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Ukládám...' : 'Uložit zápis'}
            </button>
          </form>
        </div>

        
      </div>
    </div>
  );
};
